import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import React, { useCallback, useEffect, useMemo } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { HeadcountChange } from '../../types';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

import {
  getSelectedAccount,
  updateProjectedHeadcount,
} from '../../redux/account';
import {
  fetchHeadcountChanges,
  getActiveId,
  getIsLoading,
  getHeadcountChanges,
  saveHeadcountChanges,
} from '../../redux/headcountChanges';

import _ from 'lodash';
import { v4 as uuidV4 } from 'uuid';
import HeadcountChangeGroup from './components/HeadcountChangeGroup';
import { PageWrapper } from '../../components';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { COLORS } from '../../utils/colors';

export interface HeadcountChangeFormik {
  headcountChanges: HeadcountChange[];
}
interface Props {
  isModalOpen: boolean;
  handleCloseModal: () => void;
}

const EditHeadcountChangeModal: React.FC<Props> = ({
  isModalOpen,
  handleCloseModal,
}) => {
  const dispatch = useAppDispatch();
  const { projectId } = useParams();

  const headcountChangesData = useAppSelector(getHeadcountChanges);
  const isLoading = useAppSelector(getIsLoading);
  const activeId = useAppSelector(getActiveId);
  const account = useAppSelector(getSelectedAccount);

  const initialValues: HeadcountChangeFormik = useMemo(() => {
    return {
      headcountChanges: headcountChangesData,
    };
  }, [headcountChangesData]);

  const formik = useFormik<HeadcountChangeFormik>({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup.object().shape({
      headcountChanges: yup
        .array()
        .of(
          yup.object().shape({
            projectId: yup
              .string()
              .nullable()
              .required('Please select a project.'),
            countChange: yup
              .number()
              .notOneOf([0], 'Cannot be zero.')
              .max(10000, 'Must be less than 10000.')
              .required('Required.'),
            date: yup
              .date()
              .min(
                DateTime.now().plus({ days: 1 }).startOf('day').toJSDate(),
                'Please select a future date (from tomorrow onward).',
              )
              .required('Please select a date.'),
            delete: yup.boolean().optional(),
            blendedRate: yup
              .number()
              .typeError('Please enter a valid blended rate.')
              .positive('Must be greater than zero.')
              .max(10000, 'Must be less than 10000.')
              .required('Required.'),
            blendedPayRate: yup
              .number()
              .transform((value, originalValue) => {
                // If the input is an empty string, convert it to null
                return originalValue === '' ? null : value;
              })
              .nullable() // Allows the value to be null
              .typeError('Please enter a valid blended pay rate.')
              .positive('Must be greater than zero.')
              .max(10000, 'Must be less than 10000.')
              .optional(),
          }),
        )
        .test(
          'unique-date-project',
          'Each project can have only one projected headcount entry per date.',
          (values) => {
            if (!values || values.length === 0) return true;
            const seen = new Set();
            return values?.every(({ date, projectId, delete: isDeleted }) => {
              if (!isDeleted) {
                const key = `${date}-${projectId}`;
                if (seen.has(key)) {
                  return false;
                }
                seen.add(key);
              }
              return true;
            });
          },
        )
        .test(
          'non-negative-cumulative',
          'The total headcount (actual + projected) cannot be less than zero for a project.',
          function (values) {
            if (!account) return false;
            if (!values || values.length === 0) return true;

            const actualMembers: Record<string, number> = Object.fromEntries(
              account.projects.map((p) => [p.id, p.members?.length ?? 0]),
            );

            const sortedHC = [...values].sort((a, b) => {
              if (!a.date || !b.date) return 0;
              return new Date(a.date).getTime() - new Date(b.date).getTime();
            });

            const runningTotals: Record<string, number> = {};

            for (const change of sortedHC) {
              const id = change.tempProjectId ?? change.projectId;
              if (change.delete || !id || !change.date) continue;

              runningTotals[id] =
                (runningTotals[id] || 0) + (change.countChange ?? 0);

              const base = actualMembers[id] || 0;
              if (base + runningTotals[id] < 0) {
                return false;
              }
            }

            return true;
          },
        ),
    }),
    onSubmit: (values) => {
      const updatedValues: HeadcountChange[] = values.headcountChanges
        .map((headcountChange) => ({
          id: headcountChange.id,
          countChange: Number(headcountChange.countChange),
          blendedRate: Number(headcountChange.blendedRate),
          blendedPayRate: headcountChange.blendedPayRate
            ? Number(headcountChange.blendedPayRate)
            : null,
          projectId:
            headcountChange?.tempProjectId || headcountChange.projectId,
          date: DateTime.fromISO(headcountChange.date).toFormat('yyyy-LL-dd'),
          delete: headcountChange.delete,
        }))
        .sort((a, b) => (a.date > b.date ? 1 : -1));
      values.headcountChanges = updatedValues;
      dispatch(updateProjectedHeadcount(updatedValues));
      dispatch(
        saveHeadcountChanges({
          projectId: projectId ?? '',
          headcountChanges: updatedValues,
        }),
      );
      resetForm();
    },
    validateOnMount: false,
    validateOnChange: true,
  });

  const headcountChangesGrouped: {
    [key: string]: HeadcountChange[];
  } = useMemo(() => {
    // Filter out soft deleted headcount changes
    const activeHeadcountChanges = formik.values.headcountChanges;

    // Separate new entries from saved entries
    const savedChanges = activeHeadcountChanges.filter(
      (headCountChange) => headCountChange.id, // Assuming `id` is defined for saved entries
    );

    const newChanges = activeHeadcountChanges.filter(
      (headCountChange) => !headCountChange.id, // Assuming `id` is undefined for new entries
    );

    // Group saved changes by projectId
    const groupedSavedChanges = _.groupBy(savedChanges, 'projectId');

    if (newChanges.length) groupedSavedChanges['new'] = newChanges;

    return groupedSavedChanges;
  }, [formik.values.headcountChanges]);

  useEffect(() => {
    // Account id must always be present
    if (!account?.id) return;
    if (activeId === (projectId || account?.id)) return;

    const projectIds = projectId
      ? [projectId]
      : account?.projects?.map((project) => project.id) || [];

    if (projectIds.length > 0 && !isLoading) {
      dispatch(
        fetchHeadcountChanges({
          projectIds,
          accountId: account.id,
          activeId: projectId ?? account.id,
        }),
      );
    }
  }, [account, projectId, activeId, dispatch, isLoading]);

  const addProjectedHeadcount = useCallback(() => {
    formik.setValues((previousValue) => ({
      headcountChanges: [
        ...previousValue.headcountChanges,
        {
          projectId: projectId ?? null,
          date: '',
          countChange: 0,
          tempId: uuidV4(),
          blendedRate: 1,
          blendedPayRate: null,
        },
      ],
    }));
  }, [formik, projectId]);

  const findIndex = useCallback(
    (id: string) => {
      const index = formik.values.headcountChanges.findIndex(
        (headcount) => headcount.id === id || headcount.tempId === id,
      );

      return index;
    },
    [formik.values.headcountChanges],
  );

  const handleDeleteHeadcount = useCallback(
    (indexToBeDeleted?: number, id?: string) => {
      if (id) {
        const fieldPath = `headcountChanges[${indexToBeDeleted}].delete`;
        formik.setFieldValue(fieldPath, true);
        formik.setFieldTouched(fieldPath, true, false);
      } else {
        formik.setFieldValue(
          'headcountChanges',
          formik.values.headcountChanges.filter(
            (_, headcountChangeIndex) =>
              headcountChangeIndex !== indexToBeDeleted,
          ),
        );
      }
    },
    [formik],
  );

  const handleDataChange = useCallback(
    (field: string, value: number | string | null, index: number) => {
      const fieldPath = `headcountChanges[${index}].[${field}]`;
      formik.setFieldValue(fieldPath, value);
      formik.setFieldTouched(fieldPath, true, false);
    },
    [formik],
  );

  const resetForm = useCallback(() => {
    formik.resetForm();
    handleCloseModal();
  }, [formik, handleCloseModal]);

  return (
    <Dialog
      fullWidth
      onClose={handleCloseModal}
      open={isModalOpen}
      scroll="body"
      maxWidth="lg"
    >
      <DialogTitle>Edit projected headcount </DialogTitle>
      <CloseOutlinedIcon
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 22,
          top: 21,
          color: 'grey',
          cursor: 'pointer',
        }}
        onClick={resetForm}
        fontSize="small"
      ></CloseOutlinedIcon>

      <DialogContent sx={{ maxHeight: 600 }}>
        <PageWrapper isLoading={isLoading} sx={{ paddingBlock: 0 }}>
          {_.isEmpty(headcountChangesGrouped) && (
            <Box
              alignItems={'center'}
              display={'flex'}
              gap={1.5}
              sx={{
                backgroundColor: COLORS.HIGHLIGHTS.NOTE,
                padding: 1.5,
                borderRadius: 2,
              }}
            >
              <WarningAmberIcon sx={{ color: COLORS.BRAND.PRIMARY }} />
              <Typography variant="h4" color="text.secondary">
                Add headcount change for a better forcasting.
              </Typography>
            </Box>
          )}

          {Object.keys(headcountChangesGrouped).map(
            (projectIdOrNew) =>
              headcountChangesGrouped[projectIdOrNew].length > 0 && (
                <HeadcountChangeGroup
                  isSingleProjectSelected={!!projectId}
                  findIndex={findIndex}
                  key={projectIdOrNew}
                  hcGroupKey={projectIdOrNew}
                  headcountChanges={headcountChangesGrouped[projectIdOrNew]}
                  handleDataChange={handleDataChange}
                  handleDeleteHeadcount={handleDeleteHeadcount}
                  projects={account?.projects}
                  errors={formik.errors}
                  touched={formik.touched}
                />
              ),
          )}

          <Box sx={{ px: projectId ? 0 : 2 }}>
            {typeof formik.errors.headcountChanges === 'string' && (
              <>
                <Typography color="error">
                  {formik.errors.headcountChanges}
                </Typography>
              </>
            )}
          </Box>
        </PageWrapper>
      </DialogContent>

      <DialogActions
        sx={{ alignItems: 'stretch', justifyContent: 'space-between' }}
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Button onClick={addProjectedHeadcount}>Add entry</Button>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="stretch"
          justifyContent="space-between"
          gap={1}
        >
          <Button onClick={resetForm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={formik.submitForm}
            disabled={!formik.dirty}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditHeadcountChangeModal;
